import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, getStoredLanguage, setStoredLanguage, getTranslation, isRTL } from '@/utils/i18n';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(getStoredLanguage());

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    setStoredLanguage(newLanguage);
    
    // Update document direction for RTL support
    document.documentElement.dir = isRTL(newLanguage) ? 'rtl' : 'ltr';
    document.documentElement.lang = newLanguage;
  };

  const t = (key: string): string => {
    return getTranslation(key, language);
  };

  useEffect(() => {
    // Set initial document direction and language
    document.documentElement.dir = isRTL(language) ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    isRTL: isRTL(language)
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Hook for easy translation
export const useTranslation = () => {
  const { t, language, setLanguage, isRTL } = useLanguage();
  
  return {
    t,
    language,
    setLanguage,
    isRTL,
    // Convenience methods
    toggleLanguage: () => {
      setLanguage(language === 'en' ? 'hi' : 'en');
    },
    isEnglish: language === 'en',
    isHindi: language === 'hi'
  };
};

export default LanguageProvider;
